import { usePutSkill } from "@/api/skills";
import { cn } from "@/utils";
import {
  MAX_SKILL_DESCRIPTION_LENGTH,
  skillDescriptionLength,
  truncateSkillDescription,
} from "@/utils/skillDescription";
import { type Skill } from "@cohesive/api-client";
import { RichTextEditor } from "@cohesive/ui-components";
import { useRef, useState } from "react";
import toast from "react-hot-toast";

/**
 * The skill's description, pinned at the top of the detail pane and edited with
 * the same RichTextEditor surface as the markdown files for a consistent feel.
 * The description is a plain-text string, so the editor runs in plain mode.
 * Saving is a plain PUT — the server keeps the SKILL.md frontmatter in sync.
 *
 * Sized to its content (a couple of lines, growing to a cap then scrolling)
 * rather than filling the pane, so the file section below keeps its space.
 * Like SkillMarkdownEditor, the editor is seeded once (the parent keys this
 * component per skill) so the controlled value never churns mid-edit, and the
 * draft is saved on blur.
 */
export function SkillDescriptionEditor({ skill }: { skill: Skill }) {
  const [seed] = useState(skill.description ?? "");
  const draft = useRef(seed);
  const [charCount, setCharCount] = useState(() =>
    skillDescriptionLength(seed),
  );
  const [overflowed, setOverflowed] = useState(false);

  const { mutateAsync: updateSkill } = usePutSkill({
    onError: (error) => toast.error(error.message || "Failed to update skill"),
  });

  const save = async () => {
    const next = draft.current.trim();
    if (next === (skill.description ?? "").trim()) return;
    await updateSkill({ id: skill.id, data: { description: next } });
  };

  const nearCap = charCount >= MAX_SKILL_DESCRIPTION_LENGTH - 60;

  return (
    <div className="flex flex-col">
      <RichTextEditor
        value={seed}
        onChange={(text) => {
          // The saved value is hard-capped; make the cap visible instead of
          // silently dropping overflow.
          const rawLength = skillDescriptionLength(text);
          setOverflowed(rawLength > MAX_SKILL_DESCRIPTION_LENGTH);
          setCharCount(Math.min(rawLength, MAX_SKILL_DESCRIPTION_LENGTH));
          draft.current = truncateSkillDescription(text);
        }}
        onEditorBlur={save}
        mode="plain"
        showToolbar={false}
        enableSkills={false}
        enableEntities={false}
        enablePlaceholders={false}
        placeholder="Add a description…"
        placeholderClassName="px-0 py-1 text-base text-muted-foreground/50"
        namespace={`skill-description-${skill.id}`}
        className="flex flex-col border-0 bg-transparent"
        editorClassName="min-h-0 max-h-40 overflow-y-auto px-0 py-1 text-base leading-relaxed text-foreground"
      />
      {(overflowed || nearCap) && (
        <p
          className={cn(
            "pt-0.5 text-xs",
            overflowed ? "text-destructive" : "text-muted-foreground",
          )}
        >
          {charCount}/{MAX_SKILL_DESCRIPTION_LENGTH}
          {overflowed && " — text beyond the limit won't be saved"}
        </p>
      )}
    </div>
  );
}
